import React, { SyntheticEvent, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  Typography,
  IconButton,
  TextField,
  MultiLineTextField,
  CloseIcon,
} from 'drift-ui';
import {
  CarrierAccesPayload,
  closePermisssionDialog,
  getPermissionsLevel,
  useRequestCarrierAccessMutation,
} from '../../services/freightPermissionService';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import iconGlass from '../assets/Glass.svg';
import { useTranslation } from 'react-i18next';
import styles from './UserPermissionsLevelForm.scss';
import cx from 'classnames';

interface UserPermissionsLevelFormProps {
  currentUserEmail: string;
}

const UserPermissionsLevelForm: React.FC<UserPermissionsLevelFormProps> = ({ currentUserEmail }) => {
  const [loading, setLoading] = useState(false);
  const [showSuccessUpgrade, setShowSuccessUpgrade] = useState(false);

  const { t } = useTranslation('common', { keyPrefix: 'contact_us_page.freight_request_access_form' });

  const { dialogOpen } = useSelector((state: RootState) => state.permissionDialog);
  const { data } = useSelector((state: RootState) => state.carrierPermissionsData);
  const dispatch = useDispatch();

  const [comments, setComments] = useState('');

  const permissionLevel = data && getPermissionsLevel(data.companyPermissions);

  const [requesCarrierAccess] = useRequestCarrierAccessMutation();

  const titleText: string = showSuccessUpgrade ? t('upgrade_request_success_title') : t('requesting_upgrade_title');

  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload: CarrierAccesPayload = {
      userEmail: currentUserEmail,
      permissionRequested: permissionLevel!,
      currentPermission: 'No Freight',
      primaryEmail: data?.companyAdminEmail || '',
      comments,
    };

    try {
      await requesCarrierAccess(payload);
      setShowSuccessUpgrade(true);
    } catch (error) {
      console.error('Error submitting form', error);
    } finally {
      setLoading(false);
    }
  };

  const errorCommentsLength = comments.length > 250;

  const handleDialogOnClosed = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setComments('');
    setShowSuccessUpgrade(false);
    dispatch(closePermisssionDialog());
  };

  return (
    <Dialog
      open={dialogOpen}
      onMouseDown={(e) => e.stopPropagation()}
      className={cx(styles.dialogContainer, showSuccessUpgrade ? styles.upgraded : styles.standard)}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle className={styles.dialogTitle}>{titleText}</DialogTitle>

      <IconButton className={styles.closeButton} onClick={handleDialogOnClosed}>
        <CloseIcon />
      </IconButton>

      {!showSuccessUpgrade ? (
        <DialogContent>
          <Typography className={styles.instructionsText}>{t('upgrade_request_instructions')}</Typography>

          <form onSubmit={sendForm}>
            <Box className={styles.userForm}>
              <Box className={styles.leftColumn}>
                <TextField value={data?.companyAdminName || ''} disabled label={t('company_admin_name_label')} />
                <TextField value={data?.companyAdminEmail || ''} disabled label={t('company_admin_email_label')} />
                <TextField value={permissionLevel || ''} disabled label={t('company_admin_permission_label')} />
                <TextField value="No Freight" disabled label={t('user_permission_label')} />
              </Box>

              <Box className={styles.rightColumn}>
                <MultiLineTextField
                  minRows={10}
                  maxRows={10}
                  label="Comments"
                  value={comments}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComments(e.target.value)}
                  error={errorCommentsLength}
                  placeholder={t('upgrade_reason_placeholder', { permissionLevel })}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { maxLength: 251 },
                  }}
                  className={styles.multiLineTextField}
                />

                <Box className={styles.helperTextContainer}>
                  <Typography className={cx(styles.helperText, errorCommentsLength && styles.error)}>
                    {t('optional_label')}
                  </Typography>
                  <Typography
                    className={cx(styles.helperText, errorCommentsLength && styles.error)}
                  >{`${comments.length}/250`}</Typography>
                </Box>
              </Box>
            </Box>

            <Box className={styles.buttonsContainer}>
              <Button variant="outlined" onClick={handleDialogOnClosed}>
                {t('cancel_button')}
              </Button>
              <Button
                className={styles.submitButton}
                variant="contained"
                type="submit"
                loading={loading}
                disabled={errorCommentsLength}
              >
                {t('send_request_button')}
              </Button>
            </Box>
          </form>
        </DialogContent>
      ) : (
        <DialogContent>
          <Box className={styles.successContainer}>
            <Box className={styles.iconWrapper}>
              <img src={iconGlass} alt="Glass Icon" />
            </Box>
            <Box className={styles.successTextWrapper}>
              <Typography fontSize={18}>{t('waiting_on_admin_approval')}</Typography>
            </Box>
          </Box>
          <Box className={styles.buttonsContainer}>
            <Button variant="contained" className={styles.doneButton} onClick={handleDialogOnClosed}>
              Done
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UserPermissionsLevelForm;
